import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createUser, updateUser } from '../../services/apiUsers';

const ROLES = [
    { value: 'agent', label: 'Vendedor' },
    { value: 'manager', label: 'Manager de Ventas' },
    { value: 'admin', label: 'Administrador' },
];

export default function UserModal({ isOpen, onClose, user = null }) {
    const qc = useQueryClient();
    const isEditing = Boolean(user);

    const [form, setForm] = useState({
        name: '',
        email: '',
        password: '',
        role: 'agent',
        phone: '',
        max_leads: 50,
        is_active: true,
    });

    const [error, setError] = useState('');

    useEffect(() => {
        if (user) {
            setForm({
                name: user.name ?? '',
                email: user.email ?? '',
                password: '',
                role: user.role ?? 'agent',
                phone: user.phone ?? '',
                max_leads: user.max_leads ?? 50,
                is_active: user.is_active ?? true,
            });
        } else {
            setForm({ name: '', email: '', password: '', role: 'agent', phone: '', max_leads: 50, is_active: true });
        }
        setError('');
    }, [user, isOpen]);

    const mutation = useMutation({
        mutationFn: isEditing ? updateUser : createUser,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['manager-users'] });
            qc.invalidateQueries({ queryKey: ['manager-dashboard'] });
            onClose();
        },
        onError: (err) => {
            setError(err.response?.data?.message ?? 'Error al guardar el usuario.');
        },
    });

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = () => {
        setError('');
        if (!form.name.trim() || !form.email.trim()) return setError('Nombre y email son requeridos.');
        if (!isEditing && !form.password.trim()) return setError('La contraseña es requerida para nuevos usuarios.');

        const payload = { ...form, max_leads: parseInt(form.max_leads) };
        if (isEditing) {
            if (!payload.password) delete payload.password;
            mutation.mutate({ id: user.id, ...payload });
        } else {
            mutation.mutate(payload);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
            <div className="relative w-full max-w-lg bg-white dark:bg-background-dark rounded-xl shadow-2xl overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-8 py-6 border-b border-slate-100 dark:border-primary/10">
                    <div className="flex items-center gap-3">
                        <div className="size-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                            <span className="material-symbols-outlined">{isEditing ? 'manage_accounts' : 'person_add'}</span>
                        </div>
                        <h2 className="text-xl font-bold">{isEditing ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Body */}
                <div className="px-8 py-6 space-y-5 overflow-y-auto max-h-[60vh]">
                    {error && (
                        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/50 rounded-lg text-red-700 dark:text-red-400 text-sm">
                            <span className="material-symbols-outlined text-[18px]">error</span>
                            {error}
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Nombre Completo</label>
                            <input
                                name="name" value={form.name} onChange={handleChange}
                                className="w-full h-11 rounded-lg border-slate-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:border-primary focus:ring-primary/20"
                                placeholder="Ej: María García"
                            />
                        </div>
                        <div className="col-span-2">
                            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Email</label>
                            <input
                                name="email" value={form.email} onChange={handleChange} type="email"
                                className="w-full h-11 rounded-lg border-slate-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:border-primary focus:ring-primary/20"
                                placeholder="correo@empresa.com"
                            />
                        </div>
                        <div className="col-span-2">
                            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                                {isEditing ? 'Nueva Contraseña (dejar en blanco para no cambiar)' : 'Contraseña'}
                            </label>
                            <input
                                name="password" value={form.password} onChange={handleChange} type="password"
                                className="w-full h-11 rounded-lg border-slate-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:border-primary focus:ring-primary/20"
                                placeholder="Mínimo 8 caracteres"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Rol</label>
                            <select
                                name="role" value={form.role} onChange={handleChange}
                                className="w-full h-11 rounded-lg border-slate-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:border-primary focus:ring-primary/20"
                            >
                                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Límite de Leads</label>
                            <input
                                name="max_leads" value={form.max_leads} onChange={handleChange} type="number" min={1} max={500}
                                className="w-full h-11 rounded-lg border-slate-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:border-primary focus:ring-primary/20"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Teléfono</label>
                            <input
                                name="phone" value={form.phone} onChange={handleChange}
                                className="w-full h-11 rounded-lg border-slate-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:border-primary focus:ring-primary/20"
                                placeholder="+58414..."
                            />
                        </div>
                        <div className="flex items-center gap-3 pt-6">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox" name="is_active" checked={form.is_active} onChange={handleChange}
                                    className="w-4 h-4 rounded accent-primary"
                                />
                                <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Usuario activo</span>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-8 py-5 bg-slate-50 dark:bg-slate-900/50 flex items-center justify-end gap-4 border-t border-slate-100 dark:border-primary/10">
                    <button onClick={onClose} className="px-6 py-2.5 text-slate-500 font-bold hover:text-slate-700 dark:hover:text-slate-300 transition-colors rounded-lg">
                        Cancelar
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={mutation.isPending}
                        className="px-8 py-2.5 bg-primary hover:bg-primary/90 text-white font-bold rounded-lg shadow-lg shadow-primary/20 transition-all flex items-center gap-2 disabled:opacity-70"
                    >
                        {mutation.isPending ? (
                            <span className="material-symbols-outlined text-[20px] animate-spin">refresh</span>
                        ) : (
                            <span className="material-symbols-outlined text-[20px]">save</span>
                        )}
                        {isEditing ? 'Guardar Cambios' : 'Crear Usuario'}
                    </button>
                </div>
            </div>
        </div>
    );
}
